/**
 * Persistent memory tracker that survives page refreshes
 */

interface MemorySnapshot {
  timestamp: number;
  used: number;
  total: number;
  limit: number;
  refreshCount: number;
  sessionId: string;
}

class PersistentMemoryTracker {
  private static instance: PersistentMemoryTracker;
  private snapshots: MemorySnapshot[] = [];
  private refreshCount = 0;
  private sessionId = '';
  private isInitialized = false;

  private constructor() {
    this.initialize();
  }

  static getInstance(): PersistentMemoryTracker {
    if (!PersistentMemoryTracker.instance) {
      PersistentMemoryTracker.instance = new PersistentMemoryTracker();
    }
    return PersistentMemoryTracker.instance;
  }

  private initialize(): void {
    if (typeof window === 'undefined') return;

    // Get or create session ID
    this.sessionId = this.getOrCreateSessionId();
    
    // Load refresh count from sessionStorage
    this.refreshCount = this.getRefreshCount();
    
    // Increment refresh count
    this.refreshCount++;
    this.saveRefreshCount();

    // Take initial snapshot
    this.takeSnapshot();

    // Set up beforeunload listener
    window.addEventListener('beforeunload', () => {
      this.takeSnapshot();
    });

    this.isInitialized = true;
    console.log(`🔄 Memory Tracker initialized - Session: ${this.sessionId}, Refresh: ${this.refreshCount}`);
  }

  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem('memory-tracker-session-id');
    if (!sessionId) {
      sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('memory-tracker-session-id', sessionId);
    }
    return sessionId;
  }

  private getRefreshCount(): number {
    const count = sessionStorage.getItem('memory-tracker-refresh-count');
    return count ? parseInt(count, 10) : 0;
  }

  private saveRefreshCount(): void {
    sessionStorage.setItem('memory-tracker-refresh-count', this.refreshCount.toString());
  }

  private takeSnapshot(): void {
    if (typeof window === 'undefined' || !('memory' in performance)) return;

    const memory = (performance as { 
      memory: { 
        usedJSHeapSize: number; 
        totalJSHeapSize: number; 
        jsHeapSizeLimit: number; 
      } 
    }).memory;
    const snapshot: MemorySnapshot = {
      timestamp: Date.now(),
      used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
      total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
      limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
      refreshCount: this.refreshCount,
      sessionId: this.sessionId,
    };

    this.snapshots.push(snapshot);
    
    // Keep only last 20 snapshots
    if (this.snapshots.length > 20) {
      this.snapshots.shift();
    }

    // Save to sessionStorage for persistence
    this.saveSnapshots();

    // Analyze for leaks
    this.analyzeForLeaks();
  }

  private saveSnapshots(): void {
    try {
      sessionStorage.setItem('memory-tracker-snapshots', JSON.stringify(this.snapshots));
    } catch (error) {
      console.warn('Failed to save memory snapshots:', error);
    }
  }

  private loadSnapshots(): void {
    try {
      const saved = sessionStorage.getItem('memory-tracker-snapshots');
      if (saved) {
        this.snapshots = JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Failed to load memory snapshots:', error);
    }
  }

  private analyzeForLeaks(): void {
    if (this.snapshots.length < 2) return;

    const current = this.snapshots[this.snapshots.length - 1];
    const previous = this.snapshots[this.snapshots.length - 2];
    
    const memoryIncrease = current.used - previous.used;
    
    if (memoryIncrease > 20) {
      console.warn('🚨 Memory leak detected on refresh:', {
        sessionId: current.sessionId,
        refreshCount: current.refreshCount,
        memoryIncrease: `${memoryIncrease}MB`,
        currentMemory: `${current.used}MB`,
        previousMemory: `${previous.used}MB`,
        timestamp: new Date(current.timestamp).toLocaleTimeString(),
      });

      // Log potential causes
      this.logPotentialCauses();
    } else if (memoryIncrease > 0) {
      console.log('📊 Memory increase on refresh:', {
        refreshCount: current.refreshCount,
        memoryIncrease: `${memoryIncrease}MB`,
        currentMemory: `${current.used}MB`,
      });
    }
  }

  private logPotentialCauses(): void {
    console.log('🔍 Potential causes of memory leak:');
    
    // Check for active timers
    const activeTimers = this.countActiveTimers();
    if (activeTimers > 0) {
      console.warn(`• ${activeTimers} active timers detected`);
    }

    // Check for DOM nodes
    const domNodes = document.querySelectorAll('*').length;
    if (domNodes > 2000) {
      console.warn(`• High DOM node count: ${domNodes}`);
    }

    // Check for event listeners
    const eventListeners = this.estimateEventListeners();
    if (eventListeners > 50) {
      console.warn(`• High event listener count: ${eventListeners}`);
    }

    // Check for React components
    const reactComponents = this.countReactComponents();
    if (reactComponents > 100) {
      console.warn(`• High React component count: ${reactComponents}`);
    }

    // Check for large objects in memory
    if (typeof window !== 'undefined' && 'gc' in window) {
      console.log('• Garbage collection available - try running gc()');
    }

    // Check for memory usage vs Chrome reported usage
    this.checkMemoryDiscrepancy();
  }

  private countActiveTimers(): number {
    return (window as { __activeTimers?: number }).__activeTimers || 0;
  }

  private estimateEventListeners(): number {
    return document.querySelectorAll('[onclick], [onchange], [oninput], [onload]').length;
  }

  private countReactComponents(): number {
    // Count React components by looking for data attributes
    const components = document.querySelectorAll('[data-react-component], [data-testid]');
    return components.length;
  }

  private checkMemoryDiscrepancy(): void {
    // This is a rough estimate - Chrome reports total memory usage including
    // DOM, images, CSS, etc. while performance.memory only reports JS heap
    const jsMemory = this.getCurrentMemory();
    console.log(`• JS Memory: ${jsMemory}MB (performance.memory)`);
    console.log(`• Chrome reports higher usage due to DOM, images, CSS, etc.`);
    console.log(`• This is normal - the leak is in JS heap, not total memory`);
  }

  getCurrentMemory(): number {
    if (typeof window === 'undefined' || !('memory' in performance)) return 0;
    const memory = (performance as { memory: { usedJSHeapSize: number } }).memory;
    return Math.round(memory.usedJSHeapSize / 1024 / 1024);
  }

  getRefreshCountValue(): number {
    return this.refreshCount;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  getSnapshots(): MemorySnapshot[] {
    return [...this.snapshots];
  }

  getMemoryTrend(): number {
    if (this.snapshots.length < 2) return 0;
    const current = this.snapshots[this.snapshots.length - 1];
    const first = this.snapshots[0];
    return current.used - first.used;
  }

  forceGarbageCollection(): void {
    if (typeof window !== 'undefined' && 'gc' in window) {
      (window as { gc: () => void }).gc();
      console.log('🧹 Garbage collection forced');
    } else {
      console.log('🧹 Garbage collection not available');
    }
  }

  clearData(): void {
    this.snapshots = [];
    this.refreshCount = 0;
    sessionStorage.removeItem('memory-tracker-session-id');
    sessionStorage.removeItem('memory-tracker-refresh-count');
    sessionStorage.removeItem('memory-tracker-snapshots');
    console.log('🧹 Memory tracker data cleared');
  }
}

// Auto-start in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  PersistentMemoryTracker.getInstance();
}

export { PersistentMemoryTracker };

